<script lang="ts">
	import { GoBackButton, AutoComplete } from "@hitit/new_ui/src";
	import ContainerLoading from "@hitit/hub/src/v7/components/common/ContainerLoading.svelte";
	import Collection from "@hitit/hub/src/v7/components/product/Collection.svelte";
	import ActionWithCollections from "@hitit/hub/src/v7/components/product/ActionWithCollections.svelte";
	import EditViewer from "@hitit/hub/src/v7/components/product/Option/EditViewer.svelte";
	import SetupCollection from "@hitit/hub/src/v7/components/product/Option/SetupCollection.svelte";
	import OptionCollection from "@hitit/hub/src/v7/class/StoreItem/OptionCollection";
	import DetailViewer from "@hitit/hub/src/v7/components/product/Option/DetailViewer.svelte";
	import AreYouSure from "@hitit/hub/src/v7/components/common/AreYouSure.svelte";
	import MultipleEditViewer from "@hitit/hub/src/v7/components/product/Option/MultipleEditViewer.svelte";
	//Utils
	import { api, t, notifications } from "@hitit/hub/src/stores";
	import { popup, saveStatus } from "@hitit/hub/src/v7/store";
	import { onDestroy } from "svelte";
	import { getKeyInTranslation, scrollTopBy } from "@hitit/hub/src/v7/utils";
	import OptionItem from "@hitit/hub/src/v7/class/StoreItem/OptionItem";
	import OptionCategory from "@hitit/hub/src/v7/class/StoreItem/OptionCategory";
	import type { OptionCategoryData, OptionCollectionItemDTO } from "@hitit/api";

	//Props
	export let storeId = "";

	const tempReg = /^\|TEMP_ITEM\|/;

	//Variables
	let loading = false;
	let editmode = false;
	let multipleSelectMode = false;
	let multipleSelectEdit = false;
	let product: OptionCollection;
	let setupCategory: OptionCategory | null = null;
	let selectedItems: OptionItem[] = [];
	let selectedCategoryUid = "";

	//Utils
	const mobileSaveHandler = () => {
		if (window.innerWidth < 1024) selectedItems = [];
	};

	//Autocomplete
	const autocompleteCallback = (uid: string, id: string) => {
		selectedCategoryUid = uid;
		setTimeout(() => {
			const item = document.getElementById(`${id}`);
			if (item) {
				item.classList.add("found");
				setTimeout(() => {
					item.classList.remove("found");
				}, 2000);
			}
		}, 20);
	};

	const autocompleteHandler = (search: string) => {
		const items = product.findOption(search);
		return Promise.resolve(
			items.map((v) => {
				const name =
					getKeyInTranslation(v.getTranslation(), "name", "ko") || "";
				return {
					name,
					callback: () => autocompleteCallback(v.uid || "", v.id || ""),
				};
			})
		);
	};

	// ActionWithCollections Handler
	const collectionTextClickHandler = () =>
		(selectedItems = !multipleSelectMode ? [] : selectedItems);

	const multipleEditHandler = () => {
		const condition = !multipleSelectEdit && selectedItems.length === 0;
		multipleSelectEdit = condition ? multipleSelectEdit : !multipleSelectEdit;
	};
	const multipleModeChangeHandler = () => {
		const condition = multipleSelectMode;
		selectedItems = condition
			? []
			: selectedItems.filter((v) => !tempReg.test(v.id || ""));
		multipleSelectMode = !multipleSelectMode;
	};

	// PopupHandler
	const deleteMultipleOptionItemPopupHandler = () => {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.options.you_want_to_delete",
				accept: () => multipleItemDelete(),
				cancel: () => ($popup = undefined),
			},
		};
	};
	const deleteSingleOptionItemPopupHanlder = (e: { detail: OptionItem }) => {
		const selectedOptionCategory = selectedCategory as OptionCategory;
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.options.you_want_to_delete_item",
				accept: () => optionItemDelete(selectedOptionCategory, e.detail.uid),
				cancel: () => ($popup = undefined),
			},
		};
	};
	const deleteCollectionPopupHandler = (e: { detail: OptionCategory }) => {
		$popup = {
			component: AreYouSure,
			props: {
				textTranslation: "global.product.options.you_want_to_delete",
				accept: () => deleteCollection(e.detail),
				cancel: () => ($popup = undefined),
			},
		};
	};

	// Create New Option , Collection
	const addNewCollection = async (e: { detail: string }) => {
		try {
			$saveStatus.options = true;
			const categories = product.getCategories();
			const displaySort =
				categories.length > 0
					? categories[categories.length - 1].displaySort + 1
					: 0;
			const category = OptionCategory.convinienceConstructor(
				e.detail,
				displaySort
			);
			const [uid, , categoryDTO] = await updateCollection(
				category.getOptionCategoryDTO()
			);
			const newCategory = new OptionCategory(categoryDTO);
			product.addNewCategory(newCategory);
			selectedItems = [];
			selectedCategoryUid = uid;
			product = product;
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
		}
	};

	const newOptionItemEvent = (e: { detail: OptionCategory }) => {
		const hasItem = e.detail.getStoreItemLength() >= 1;
		const displaySort = e.detail.getLastDisplaysort();
		const arg = OptionItem.convienceArg(
			hasItem ? displaySort + 1 : 0,
			`|TEMP_ITEM|${Math.random()}`
		);
		selectedItems = [OptionItem.convinienceConstructor(arg, "ko", e.detail)];
	};

	//Collection Handler
	const setupCategoryHandler = (e: { detail: OptionCategory }) =>
		(setupCategory = setupCategory === null ? e.detail : null);
	const onItemClickEvent = (e: { detail: OptionItem }) =>
		(selectedItems = [e.detail]);

	// Single
	const optionItemDelete = async (
		selectedCategory: OptionCategory,
		uid?: string
	) => {
		$popup = undefined;
		try {
			$saveStatus.options = true;
			const dto = selectedCategory.getOptionCategoryDTO();
			const filteredOptionCollection = dto.optionCollectionItemDTO.filter(
				(v) => v.uid !== uid
			);

			dto.optionCollectionItemDTO = filteredOptionCollection;
			const [newUid, , categoryData] = await updateCollection(dto);
			const newCategory = new OptionCategory(categoryData);
			product.replaceCategoryByUid(newUid, newCategory);
			product = product;
			selectedItems = selectedItems.filter((v) => v.uid !== uid);
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
		}
	};

	const optionItemEditHandler = async (e: {
		detail: OptionCollectionItemDTO;
	}) => {
		try {
			$saveStatus.options = true;
			const editItem = e.detail;
			const isNew = editItem.uid === undefined;
			const categoryDTOarg = selectedCategory!.getOptionCategoryDTO();
			const filteredOriginItemDTOs =
				categoryDTOarg.optionCollectionItemDTO.filter(
					(v) => v.uid !== editItem.uid
				);
			const originItemId = selectedCategory!
				.getOptions()
				.find((v) => v.uid === editItem.uid)?.id;

			editItem.id = originItemId;
			categoryDTOarg.optionCollectionItemDTO = [
				...filteredOriginItemDTOs,
				editItem,
			];

			const [uid, , categoryDTO] = await updateCollection(categoryDTOarg);
			const newCategory = new OptionCategory(categoryDTO);
			product.replaceCategoryByUid(uid, newCategory);

			if (isNew) {
				selectedItems = [];
			} else {
				afterUpdateHandleSelectItems();
			}
			product = product;
			mobileSaveHandler();
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
		}
	};

	const updateCollection = async (optionCategory: OptionCategoryData) => {
		try {
			const setOptionCollectionResponse = await $api.setOptionCollection(
				storeId,
				optionCategory
			);
			const getOptionCollectionsResponse = await $api.getStoreOptionCollection(
				storeId
			);
			const { uid, id, status } = setOptionCollectionResponse.data;
			const newCategoryData =
				getOptionCollectionsResponse.data.optionCollectionDTOs.find(
					(v) => v.id === id
				);
			const throwErrorCondition =
				status !== "OK" || newCategoryData === undefined;
			if (throwErrorCondition) throw new Error(status);

			return [uid, id, newCategoryData] as [string, string, OptionCategoryData];
		} catch (e) {
			throw e;
		}
	};

	const setupCollectionUpdateHandler = async (e: {
		detail: OptionCategoryData;
	}) => {
		try {
			$saveStatus.options = true;
			//
			const dto = e.detail;
			const originUid = dto.uid || "";
			const [, , categoryDTO] = await updateCollection(dto);
			const newCategory = new OptionCategory(categoryDTO);
			product.replaceCategoryByUid(originUid, newCategory);
			// afterUpdateHandleSelectItems();
			product = product;
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
		}
	};
	// Multiple
	const multipleViewerSaveHandler = async (e: { detail: OptionItem[] }) => {
		try {
			$saveStatus.options = true;
			const toBeModifiedCollecitons = [
				...new Set(selectedItems.map((v) => v.collection)),
			].map((v) => v.getOptionCategoryDTO());

			for (const item of e.detail) {
				const collectionUid = item.collection.uid;
				const find = toBeModifiedCollecitons.find(
					(v) => v.uid === collectionUid
				);

				if (find) {
					const filteredOptionItems = find.optionCollectionItemDTO.filter(
						(v) => v.uid !== item.uid
					);
					find.optionCollectionItemDTO = [
						...filteredOptionItems,
						item.getOptionCollectionItemDTO(),
					];
				}
			}
			for (const categoryData of toBeModifiedCollecitons) {
				const [uid, , categoryDTO] = await updateCollection(categoryData);
				const newCategory = new OptionCategory(Object.assign(categoryDTO));
				product.replaceCategoryByUid(uid || "", newCategory);
			}

			product = product;
			afterUpdateHandleSelectItems();
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			mobileSaveHandler();
			$saveStatus.options = false;
			product = product;
		}
	};

	const multipleItemDelete = async () => {
		try {
			$saveStatus.options = true;
			const toBeModifiedCollecitons = [
				...new Set(selectedItems.map((v) => v.collection)),
			].map((v) => v.getOptionCategoryDTO());

			for (const item of selectedItems) {
				const collectionUid = item.collection.uid;
				const find = toBeModifiedCollecitons.find(
					(v) => v.uid === collectionUid
				);

				if (find) {
					const filteredOptionItems = find.optionCollectionItemDTO.filter(
						(v) => v.uid !== item.uid
					);
					find.optionCollectionItemDTO = filteredOptionItems;
				}
			}

			for (const categoryData of toBeModifiedCollecitons) {
				const [uid, , categoryDTO] = await updateCollection(categoryData);
				const newCategory = new OptionCategory(Object.assign(categoryDTO));
				product.replaceCategoryByUid(uid || "", newCategory);
			}
			selectedItems = [];
			product = product;
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
			$popup = undefined;
		}
	};
	//DeleteCollection.
	const deleteCollection = async (target: OptionCategory) => {
		try {
			$popup = undefined;
			$saveStatus.options = true;
			await $api.deleteStoreItemOptionCollection(target.uid as string);
			product.deleteCategory(target);
			selectedItems = [];
			product = product;
		} catch (e) {
			errorHandler(e as Error);
		} finally {
			$saveStatus.options = false;
		}
	};

	// MultipleEditViewer
	const multipleViewerDeleteHandler = ({ detail }: { detail: string }) => {
		selectedItems = selectedItems.filter((v) => v.id !== detail);
	};

	//self
	const afterUpdateHandleSelectItems = () => {
		const newSelectedItems = selectedItems
			.map((v) => {
				if (tempReg.test(v.id || "")) {
					const parent = product.findOptionByUid(v.collection.uid || "");
					v.collection = parent as OptionCategory;
					return v;
				}
				const parent = product.findOptionByUid(v.collection.uid || "");
				const option = parent?.findOptionByuid(v.uid || "");
				return option;
			})
			.filter((v) => v !== undefined) as OptionItem[];

		selectedItems = newSelectedItems;
	};

	const errorHandler = async (error: Error) => {
		const message = error.message;
		const errorTranslation = $t(`error.DATA_WAS_BEEN_MODIFIED`);
		const errorMessage = `
			ERROR STATUS : ${message},<br>
			${errorTranslation}
		`;
		notifications.append("ERROR", errorMessage, "", undefined, "ERROR");
		console.log(error);
		await getStoreOptions(storeId);
	};

	async function getStoreOptions(storeId: string) {
		try {
			loading = true;
			const { data } = await $api.getStoreOptionCollection(storeId);
			const { optionCollectionDTOs, revision } = data;

			product = new OptionCollection(optionCollectionDTOs, revision);
			selectedCategoryUid = product
				.getNameIdItemLength()
				.reduce((prev, cur, i) => {
					if (i === 0) return cur.uid || "";

					return prev;
				}, "");
			selectedItems = [];
		} catch (e) {
			console.log(e);
		} finally {
			loading = false;
		}
	}

	$: selectedCategory = product
		? product.getCategories().find((v) => v.uid === selectedCategoryUid)
		: null;
	$: detailDisplayCondition =
		(!editmode && selectedItems.length === 1) ||
		(editmode && !multipleSelectMode && selectedItems.length === 1) ||
		(editmode &&
			multipleSelectMode &&
			selectedItems.length >= 1 &&
			multipleSelectEdit);
	$: getStoreOptions(storeId);
	onDestroy(() => {
		$popup = undefined;
	});

</script>

<div class="container">
	{#if loading}
		<ContainerLoading />
	{/if}
	{#if product}
		<form
			id="search-item"
			on:submit|preventDefault
			class:mobileHide={detailDisplayCondition}>
			<AutoComplete
				placeholder={$t('global.product.options.find_option')}
				searchCallback={autocompleteHandler} />
		</form>
		<div class="actions" class:mobileHide={detailDisplayCondition}>
			<ActionWithCollections
				placeholder={$t('global.product.options.option_name')}
				on:EDIT_CHANGE={() => (editmode = !editmode)}
				on:MULTIPLE_DELETE={deleteMultipleOptionItemPopupHandler}
				on:MULTIPLE_SELECT_MODE_CHANGE={multipleModeChangeHandler}
				on:MULTIPLE_EDIT={multipleEditHandler}
				on:CLICK_COLLECTION_TEXT={collectionTextClickHandler}
				on:NEW_COLLECTION={addNewCollection}
				bind:editmode
				bind:multipleSelectMode
				bind:selectedCollectionUid={selectedCategoryUid}
				editable={false}
				collections={product.getNameIdItemLength()} />
		</div>
		<div class="list-detail-wrapper">
			<div class="list" class:mobileHide={detailDisplayCondition}>
				{#if selectedCategory}
					<Collection
						on:COLLECTION_SETUP_EVENT={setupCategoryHandler}
						on:ITEM_CLICK_EVENT={onItemClickEvent}
						on:ITEM_DELETE_CLICK_EVENT={deleteSingleOptionItemPopupHanlder}
						on:NEW_ITEM_EVENT={newOptionItemEvent}
						bind:selectedItems
						hasSetup={Boolean(setupCategory)}
						collection={selectedCategory}
						items={selectedCategory?.getOptions()}
						editmode={editmode}
						multipleSelectMode={multipleSelectMode}>
						<div slot="setup">
							{#if setupCategory}
								<SetupCollection
									on:UPDATE_COLLECTION={setupCollectionUpdateHandler}
									on:DELETE_OPTION_CATEGORY={deleteCollectionPopupHandler}
									bind:optionCategory={selectedCategory}
									disabled={$saveStatus.options}
									clickOutsideCallBack={() => (setupCategory = null)} />
							{/if}
						</div>
					</Collection>
				{/if}
			</div>
			<div
				class="detail"
				use:scrollTopBy={detailDisplayCondition}
				class:mobileDisplayCondition={detailDisplayCondition}>
				<div class="goBack">
					<GoBackButton
						on:click={() => {
							selectedItems = [];
							multipleSelectEdit = false;
						}} />
				</div>
				{#if !editmode && selectedItems.length === 1}
					<DetailViewer item={selectedItems[0]} />
				{:else if editmode && !multipleSelectMode && selectedItems.length === 1}
					<EditViewer
						on:EDIT_ITEM_DONE={optionItemEditHandler}
						item={selectedItems[0].getClone()} />
				{:else if editmode && multipleSelectMode && selectedItems.length >= 1}
					<MultipleEditViewer
						on:UPDATE_MULTIPLE_ITEMS={multipleViewerSaveHandler}
						on:MULTIPLE_SELECTED_OPTION_DELETE={multipleViewerDeleteHandler}
						items={selectedItems.map((v) => v.getClone())} />
				{/if}
			</div>
		</div>
	{/if}
</div>

<style lang="scss">
	@import "../../variables.scss";

	.container {
		display: flex;
		flex-direction: column;
		width: 100%;
		gap: 22px;

		#search-item {
			width: 100%;
			height: 31px;
			color: #46474b;
		}

		.mobileHide {
			display: none;
		}
		.detail {
			display: none;
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			min-height: 100vh;
			max-height: 100vh;
			overflow-y: auto;
			box-sizing: border-box;
			background-color: #f0f0f4;
			padding: 70px 20px 20px 20px;
			&.mobileDisplayCondition {
				display: flex;
				flex-direction: column;
			}
		}

		.goBack {
			width: 30px;
			height: 27px;
			position: absolute;
			top: 23.2px;
			right: 20px;
		}
	}
	@include DesktopSize {
		.container {
			#search-item {
				width: calc(50% - 15.5px);
				font-size: 1.25rem;
				height: 40px;
			}

			.mobileHide {
				display: block;
			}
			.list-detail-wrapper {
				flex-basis: 100%;
				width: 100%;
				display: flex;
				gap: 20px;
				.list {
					flex-basis: calc(100% * (952 / 1594));
					flex-grow: 1;
				}
			}
			.detail {
				padding: 0;
				display: flex;
				flex-direction: column;
				position: static;
				min-height: 0;
				flex-basis: calc(100% * (642 / 1594));
				max-width: 642px;
				max-height: none;
				/* temp debuging point */
			}
			.goBack {
				display: none;
			}
		}
	}

</style>
